import React, { useEffect, useState } from 'react';
import { useWallet } from '../context/WalletProvider';
import { useToast } from '../components/Toast';
import Loader from '../components/Loader';
import { fetchUser, fetchLevels, fetchLevelMembers } from '../services/zen';
import { getNames } from '../services/api';

const LEVELS = ['Level 1', 'Level 2', 'Level 3', 'Level 4', 'Level 5'];
const PAGE = 10;

export default function Team() {
  const { provider, address, isCorrectNetwork } = useWallet();
  const toast = useToast();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [levels, setLevels] = useState(null);

  // which level is expanded (0-based) or -1
  const [openLevel, setOpenLevel] = useState(-1);
  // members[level] = { page, total, rows:[{id,name,account}], loading }
  const [members, setMembers] = useState({});

  useEffect(() => {
    let alive = true;
    if (!provider || !address || !isCorrectNetwork) { setLoading(false); return; }
    (async () => {
      try {
        setLoading(true);
        const u = await fetchUser(provider, address);
        if (!alive) return;
        setUser(u);
        if (u.registered) {
          const lv = await fetchLevels(provider, u.id);
          if (alive) setLevels(lv);
        }
      } catch (_) {} finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [provider, address, isCorrectNetwork]);

  // load a page of members for a level (chain IDs -> DB names)
  const loadPage = async (levelIdx, page) => {
    setMembers((m) => ({ ...m, [levelIdx]: { ...(m[levelIdx] || {}), loading: true } }));
    try {
      const offset = page * PAGE;
      const { ids, total } = await fetchLevelMembers(provider, user.id, levelIdx, offset, PAGE);
      const nameMap = await getNames(ids);
      const rows = ids.map((id) => ({
        id,
        name: nameMap[id]?.name || '—',
        account: nameMap[id]?.account_number || '—',
      }));
      setMembers((m) => ({ ...m, [levelIdx]: { page, total, rows, loading: false } }));
    } catch (e) {
      setMembers((m) => ({ ...m, [levelIdx]: { ...(m[levelIdx] || {}), loading: false } }));
      toast.error('Could not load members');
    }
  };

  const toggleLevel = (levelIdx) => {
    if (openLevel === levelIdx) { setOpenLevel(-1); return; }
    setOpenLevel(levelIdx);
    if (!members[levelIdx]) loadPage(levelIdx, 0);
  };

  const changePage = (levelIdx, dir) => {
    const cur = members[levelIdx];
    if (!cur) return;
    const next = cur.page + dir;
    const maxPage = Math.max(0, Math.ceil(cur.total / PAGE) - 1);
    if (next < 0 || next > maxPage) return;
    loadPage(levelIdx, next);
  };

  if (loading) return <Loader messages={['Building your network', 'Counting your team']} />;

  if (!user || !user.registered) {
    return <div><h1 className="page-title">Team</h1><div className="card"><p className="muted">Register first to build your team.</p></div></div>;
  }

  const refLink = `${window.location.origin}/account?ref=${user.accountNumber}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(refLink); toast.success('Referral link copied'); }
    catch (_) { toast.error('Could not copy link'); }
  };

  const totalTeam = levels ? levels.memberCounts.reduce((a, b) => a + b, 0) : 0;

  return (
    <div>
      <h1 className="page-title">My Team</h1>

      <div className="card">
        <div className="card-title">Your Referral Link</div>
        <div className="mono" style={{ fontSize: 12, color: 'var(--blue-bright)', wordBreak: 'break-all', marginBottom: 12 }}>{refLink}</div>
        <button className="btn gold" onClick={copy}>Copy Link</button>
      </div>

      <div className="spacer-18" />

      <div className="stat-grid">
        <div className="stat"><div className="k">Direct Referrals</div><div className="v blue">{user.directReferrals}</div></div>
        <div className="stat"><div className="k">Total Team (5 lvls)</div><div className="v gold">{totalTeam}</div></div>
      </div>

      <div className="spacer-18" />

      <div className="card-title">Members By Level</div>
      {levels && LEVELS.map((name, i) => {
        const count = levels.memberCounts[i];
        const data = members[i];
        const maxPage = data ? Math.max(0, Math.ceil(data.total / PAGE) - 1) : 0;
        const isOpen = openLevel === i;
        return (
          <div className="card" key={name} style={{ marginBottom: 10, padding: 0, overflow: 'hidden' }}>
            <button
              className={'faq-q' + (isOpen ? ' open' : '')}
              onClick={() => toggleLevel(i)}
              style={{ width: '100%' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className="lvl-badge">L{i + 1}</span>
                <span>{name}</span>
                <span className={'tag ' + (levels.eligibility[i] ? 'ok' : 'locked')} style={{ marginLeft: 4 }}>
                  {count} {count === 1 ? 'member' : 'members'}
                </span>
              </span>
              <svg className="chev" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6" /></svg>
            </button>

            {isOpen && (
              <div style={{ padding: '0 14px 14px' }}>
                {count === 0 ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>No members at this level yet.</p>
                ) : data?.loading && !data?.rows ? (
                  <p className="muted" style={{ fontSize: 12.5 }}>Loading members…</p>
                ) : data?.rows ? (
                  <>
                    <table className="tbl">
                      <thead><tr><th>User ID</th><th>Name</th><th>MT5 Acct</th></tr></thead>
                      <tbody>
                        {data.rows.map((r) => (
                          <tr key={r.id}>
                            <td style={{ color: 'var(--blue-bright)' }}>{r.id}</td>
                            <td>{r.name}</td>
                            <td>{r.account}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.total > PAGE && (
                      <div className="row-between" style={{ marginTop: 12 }}>
                        <button className="pill" onClick={() => changePage(i, -1)} disabled={data.page === 0 || data.loading}>‹ Prev</button>
                        <span className="muted" style={{ fontSize: 12 }}>Page {data.page + 1} / {maxPage + 1}</span>
                        <button className="pill" onClick={() => changePage(i, 1)} disabled={data.page >= maxPage || data.loading}>Next ›</button>
                      </div>
                    )}
                  </>
                ) : null}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
